import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface SchoolBus {
  id: number;
  reg_no: string;
  driver: string;
  driver_phone: string;
  helper: string;
  helper_phone: string;
  route: string;
  bus_type: string;
  company: string;
  contractor_phone: string;
  sort_order: number;
  status: boolean;
}

export default function SchoolBuses() {
  const navigate = useNavigate();
  const [buses, setBuses] = useState<SchoolBus[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getBuses();
  }, [page, limit, search]);

  const getBuses = async () => {
    setLoading(true);
    const response = await request.post("school-buses", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setBuses(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const busTypeLabel = (value: string) =>
    value === "contract_bus" ? "Contract Bus" : value === "school_bus" ? "School Bus" : value;

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "reg_no",
      label: "Reg. No.",
      minWidth: 140,
      format: (value) => value,
    },
    {
      id: "driver",
      label: "Driver",
      minWidth: 140,
      format: (value) => value,
    },
    {
      id: "driver_phone",
      label: "Driver Phone",
      minWidth: 130,
      format: (value) => value || "—",
    },
    {
      id: "route",
      label: "Route",
      minWidth: 180,
      format: (value) => value || "—",
    },
    {
      id: "bus_type",
      label: "Bus Type",
      minWidth: 130,
      format: (value) => (
        <Box
          component="span"
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: "999px",
            fontSize: "0.75rem",
            fontWeight: 600,
            bgcolor: value === "contract_bus" ? "#ede9fe" : "#dcfce7",
            color: value === "contract_bus" ? "#7c3aed" : "#16a34a",
          }}
        >
          {busTypeLabel(value)}
        </Box>
      ),
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 110,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/school-buses/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.reg_no }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="School Buses"
        showSearch
        searchPlaceholder="Search buses..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Bus",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/school-buses/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={buses}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Bus - " + deleteDialog?.title}
          message="Are you sure you want to delete this bus? This action cannot be undone."
          apiEndpoint={"school-buses/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getBuses();
          }}
        />
      </PageWrapper>
    </>
  );
}